import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, MessageSquare, Send, User, Truck } from 'lucide-react';
import { Skeleton } from '../ui/kit';
import { datumUndZeit, uhrzeit } from '../lib/zeit';

export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatUser, setChatUser] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/admin/conversations').then(r => { setConversations(r.data.conversations); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const openConv = async (conv) => {
    setSelected(conv);
    try {
      const r = await api.get(`/admin/conversations/${conv.id}/messages`);
      setMessages(r.data.messages);
      setChatUser(r.data.user);
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_admin: 0 } : c));
    } catch { toast.error('Fehler beim Laden'); }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !selected || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/conversations/${selected.id}/message`, { message: text.trim() });
      setMessages(prev => [...prev, r.data.message]);
      setText('');
      setConversations(prev => prev.map(c => c.id === selected.id ? { ...c, last_message: text.trim() } : c));
    } catch { toast.error('Fehler'); } finally { setSending(false); }
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_admin || 0), 0);

  return (
    // Handy: Liste ODER Gespräch (vg-md in index.css), Desktop unverändert nebeneinander.
    <div className={`vg-md vg-md-fill${selected ? ' has-selection' : ''}`} style={{ display: 'flex', height: 'calc(100vh - 80px)', gap: 0, background: '#f5f5f5', borderRadius: 16, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      {/* Conversation List */}
      <div className="vg-md-list" style={{ width: 300, background: '#ffffff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>Support Chat</h2>
          {totalUnread > 0 && <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 700, marginTop: 2 }}>{totalUnread} ungelesen</div>}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <Skeleton h={120} r={12} style={{ margin: 16 }} />}
          {!loading && conversations.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: '#4b5563', fontSize: 13 }}>Noch keine Gespräche</div>
          )}
          {conversations.map(conv => {
            const isSelected = selected?.id === conv.id;
            return (
              <div key={conv.id} onClick={() => openConv(conv)}
                style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', background: isSelected ? '#e5e7eb' : 'transparent', transition: 'background 0.15s', position: 'relative' }}>
                {conv.unread_admin > 0 && (
                  <div style={{ position: 'absolute', top: 14, right: 14, background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 8, padding: '1px 5px' }}>{conv.unread_admin}</div>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: conv.user_type === 'driver' ? '#0a0a0a22' : '#4b556322', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {conv.user_type === 'driver' ? <Truck size={16} color="#0a0a0a" /> : <User size={16} color="#4b5563" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: conv.unread_admin > 0 ? 800 : 600, color: '#0a0a0a', marginBottom: 2 }}>{conv.user_name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.last_message || 'Kein Inhalt'}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 4 }}>
                  {datumUndZeit(conv.last_message_at, { jahr: false })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat Window */}
      <div className="vg-md-detail" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <MessageSquare size={48} color="#e5e7eb" />
            <div style={{ color: '#6b7280', fontSize: 14 }}>Gespräch auswählen</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', background: '#ffffff', display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" className="vg-md-back is-icon" onClick={() => setSelected(null)} aria-label="Zurück zur Gesprächsliste"><ArrowLeft size={18} /></button>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#0a0a0a22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Truck size={18} color="#0a0a0a" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0a0a0a' }}>{chatUser?.name}</div>
                <div style={{ fontSize: 12, color: '#4b5563' }}>{selected.user_type === 'driver' ? 'Fahrer' : 'Kunde'} · {chatUser?.phone}</div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {messages.map(msg => {
                const isAdmin = msg.sender_role === 'admin';
                return (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                    <div className="vg-chat-bubble" style={{ maxWidth: '65%', padding: '10px 14px', borderRadius: isAdmin ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: isAdmin ? '#0a0a0a' : '#e5e7eb', border: isAdmin ? 'none' : '1px solid #d1d5db' }}>
                      <div style={{ fontSize: 13, color: isAdmin ? '#fff' : '#0a0a0a', lineHeight: 1.5 }}>{msg.message}</div>
                      <div style={{ fontSize: 12, color: isAdmin ? 'rgba(255,255,255,0.5)' : '#6b7280', marginTop: 4, textAlign: isAdmin ? 'right' : 'left' }}>
                        {uhrzeit(msg.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', background: '#ffffff', display: 'flex', gap: 10 }}>
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Nachricht schreiben..."
                style={{ flex: 1, background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 12, color: '#0a0a0a', padding: '11px 14px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={sendMessage} disabled={!text.trim() || sending}
                style={{ width: 44, height: 44, borderRadius: 12, background: text.trim() ? '#0a0a0a' : '#e5e7eb', border: 'none', cursor: text.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Send size={17} color={text.trim() ? '#fff' : '#6b7280'} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
